import { FormHelperText, IconButton } from "@mui/material"
import { useFormikContext } from "formik"
import { Armchair, Clock, X } from "lucide-mui"
import { DateTime } from "luxon"
import React, { useMemo } from "react"

import { DateField } from "@/components/form"
import PeopleAmountInput from "@/components/people_amount_input"
import { STATUS_PENDING, STATUS_CONFIRMED, type ReservationStatus } from "@/constants/reservations"
import i18n from "@/i18n"

import StatusField from "../shared/status_field"

import type { CreateReservationFormValues, ReservationType, TableSlot } from "./use_booking_state"

const CREATE_STATUSES: ReservationStatus[] = [STATUS_PENDING, STATUS_CONFIRMED]

type TimeSectionProps = {
  venue: any
  reservationType: ReservationType
}

const TimeSection: React.FC<TimeSectionProps> = ({ venue, reservationType }) => {
  const { values, setFieldValue } = useFormikContext<CreateReservationFormValues>()

  const activeTables = venue.tables.filter((table: any) => !table.deleted)

  const getTableCapacityWarning = useMemo(() => {
    if (values.tableSlots.length === 0) return null
    if (values.peopleAmount === "" || values.peopleAmount == null) return null
    const peopleAmount = Number(values.peopleAmount)
    if (!Number.isFinite(peopleAmount) || peopleAmount <= 0) return null

    for (const slot of values.tableSlots) {
      const table = activeTables.find((t: any) => t.id === slot.tableId)
      if (!table) continue

      if (table.minCapacity && peopleAmount < table.minCapacity) {
        return i18n.t("app.settings.venues.venue_layout.table_capacity.min_required", {
          count: table.minCapacity,
        })
      }
      if (table.maxCapacity && peopleAmount > table.maxCapacity) {
        return i18n.t("app.settings.venues.venue_layout.table_capacity.max_exceeded", {
          count: table.maxCapacity,
        })
      }
    }
    return null
  }, [values.tableSlots, values.peopleAmount, activeTables])

  const formatTimeRange = (startTime: string, endTime: string) => {
    const start = DateTime.fromISO(startTime)
    const end = DateTime.fromISO(endTime)
    return `${start.toFormat("HH:mm")} - ${end.toFormat("HH:mm")}`
  }

  const handleRemoveTableSlot = (tableId: string) => {
    void setFieldValue(
      "tableSlots",
      values.tableSlots.filter((slot: TableSlot) => slot.tableId !== tableId),
    )
  }

  return (
    <>
      <DateField
        name="date"
        label={i18n.t("app.reservation.labels.date")}
        country={venue.country}
        timezone={venue.timezone}
      />
      <PeopleAmountInput name="peopleAmount" label={i18n.t("app.reservation.labels.people_amount")} />

      {values.tableSlots.length > 0 ? (
        <div className="space-y-2">
          {values.tableSlots.map((slot) => (
            <div
              key={slot.tableId}
              className="flex items-center gap-2 bg-[#fafafb] border border-[#e5e7eb] rounded-[5px] px-3 py-2 text-sm"
            >
              <Armchair className="w-4 h-4 text-[#5f6774]" />
              <span className="font-medium text-[#374151]">{slot.tableName}</span>
              <Clock className="w-4 h-4 text-[#ACAEBA] ml-2" />
              <span className="text-[#5f6774]">{formatTimeRange(slot.startTime, slot.endTime)}</span>
              <IconButton size="small" onClick={() => handleRemoveTableSlot(slot.tableId)} className="ml-auto">
                <X className="w-4 h-4 text-[#9ca3af]" />
              </IconButton>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[#5f6774] text-sm bg-[#fafafb] border border-[#e5e7eb] rounded-[5px] px-3 py-2">
          {i18n.t("app.create_reservation.selected_tables.empty")}
        </div>
      )}

      {getTableCapacityWarning ? (
        <FormHelperText className="text-orange-500">{getTableCapacityWarning}</FormHelperText>
      ) : null}

      {reservationType === "reservation" && <StatusField statuses={CREATE_STATUSES} />}
    </>
  )
}

export default TimeSection
